/* eslint-disable @typescript-eslint/no-explicit-any */
// app/app.config.ts (standalone)
import {HttpContext, HttpClient, provideHttpClient} from '@angular/common/http';
import {APP_INITIALIZER, ApplicationConfig} from '@angular/core';
import {BrandingService} from './branding.service';
import {publicSettings} from './settings.service';
import {PublicSettings} from '@gatewee/types/system';
import {SKIP_SPINNER} from '../tokens/spinner.token';
import {firstValueFrom} from 'rxjs';

function camelToDash(input: string): string {
  return input.replace(/([A-Z])/g, '-$1').toLowerCase();
}

// DI token

const keys = ['branding', 'businessRules'] as const;

type SettingKey = (typeof keys)[number];

export interface BootstrapPublicSettingsConfig {
  baseUrl: string;
}

// helpers de snapshot
function writeSnapshot(settings: Partial<PublicSettings>) {
  try {
    for (const key of keys) {
      const value = (settings as any)[key];
      if (value !== undefined) {
        localStorage.setItem(key, JSON.stringify(value));
      }
    }
  } catch {
    // ignore quota/privacy errors
  }
}

function readSnapshot<K extends SettingKey>(
  key: K,
): PublicSettings[K] | undefined {
  try {
    const s = localStorage.getItem(key);
    return s ? (JSON.parse(s) as PublicSettings[K]) : undefined;
  } catch {
    return undefined;
  }
}

// busca um endpoint e mantém em memória + snapshot; usa snapshot/default em caso de erro
function fetchOne<K extends SettingKey>(
  key: K,
  http: HttpClient,
  baseUrl: string,
  options?: {skipSpinner?: boolean},
) {
  return async () => {
    const url = `${baseUrl}/${camelToDash(key)}`;
    const context = options?.skipSpinner
      ? new HttpContext().set(SKIP_SPINNER, true)
      : undefined;
    try {
      const res = await firstValueFrom(
        http.get<PublicSettings[K]>(url, {
          observe: 'response',
          ...(context && {context}),
        }),
      );
      const cfg = res.body;
      (window as any).__PUBLIC_SETTINGS__[key] = cfg;

      (window as any).__PUBLIC_SETTINGS__['authorized'] =
        res.headers.get('Authorized') === 'true';

      writeSnapshot({[key]: cfg} as Partial<PublicSettings>);
    } catch {
      const snap = readSnapshot(key);
      console.error('Error fetching settings');
      (window as any).__PUBLIC_SETTINGS__[key] = snap;
    }
  };
}

// Core load logic shared by bootstrap and reload
async function loadPublicSettings(
  http: HttpClient,
  brandingService: BrandingService,
  config: BootstrapPublicSettingsConfig,
  options?: {skipSpinner?: boolean},
): Promise<PublicSettings> {
  if (!(window as any).__PUBLIC_SETTINGS__) {
    (window as any).__PUBLIC_SETTINGS__ = {} as PublicSettings;
  }

  const cachedBranding =
    ((window as any).__PUBLIC_SETTINGS__ as PublicSettings).branding ??
    readSnapshot('branding');
  if (cachedBranding !== undefined) {
    (window as any).__PUBLIC_SETTINGS__.branding = cachedBranding;
  }

  // Apply immediately from cache/default to avoid blank vars on early paint.
  brandingService.init(
    (cachedBranding as PublicSettings['branding']) ?? undefined,
  );

  await Promise.all(
    keys.map(key => fetchOne(key, http, config.baseUrl, options)()),
  );

  const settings = (window as any).__PUBLIC_SETTINGS__ as PublicSettings;

  // Re-apply with latest payload. BrandingService handles undefined safely.
  brandingService.init(settings.branding);

  return settings;
}

/** Reload public settings from the backend. Useful after domain/recipient switch or config changes. */
export async function reloadPublicSettings(
  http: HttpClient,
  brandingService: BrandingService,
  config: BootstrapPublicSettingsConfig = {
    baseUrl: '/recipients-settings/public',
  },
): Promise<PublicSettings> {
  try {
    return await loadPublicSettings(http, brandingService, config, {
      skipSpinner: true,
    });
  } catch (error) {
    console.error('Error reloading public settings:', error);
    return (window as any).__PUBLIC_SETTINGS__ as PublicSettings;
  }
}

// APP_INITIALIZER: inicializa store em memória e carrega os 2 blocos em paralelo
export function bootstrapPublicSettings(
  http: HttpClient,
  brandingService: BrandingService,
  config: BootstrapPublicSettingsConfig = {
    baseUrl: '/recipients-settings/public',
  },
) {
  return async () => {
    try {
      return await loadPublicSettings(http, brandingService, config);
    } catch (error) {
      console.error('Error fetching public settings:', error);
      return (window as any).__PUBLIC_SETTINGS__;
    }
  };
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideHttpClient(),
    {
      provide: publicSettings,
      useFactory: () => (window as any).__PUBLIC_SETTINGS__ as PublicSettings,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: bootstrapPublicSettings,
      deps: [HttpClient, BrandingService],
      multi: true,
    },
  ],
};
